import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Topbar from '../components/layout/Topbar';
import { useCountry } from '../contexts/CountryContext';
import { useAuth } from '../contexts/AuthContext';
import { useTenantScope } from '../contexts/TenantScopeContext';
import { refApi, signalApi, adminApi, formatApiError } from '../lib/api';
import { INFRA_STATUSES, SIGNAL_STATUSES } from '../lib/countryConfig';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STATUS_COLOR = Object.fromEntries(INFRA_STATUSES.map((s) => [s.value, s.color]));
const SIG_COLOR = Object.fromEntries(SIGNAL_STATUSES.map((s) => [s.value, s.color]));

function divIcon(html, size = 28) {
  return L.divIcon({ className: 'gd-marker', html, iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
}

function infraMarker(inf, country) {
  const col = STATUS_COLOR[inf.statut] || '#999';
  const type = country.infrastructureTypes.find((t) => t.code === inf.type_code);
  const icon = type?.icon || '📍';
  return divIcon(
    `<div style="width:28px;height:28px;border-radius:50%;background:${col};border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-size:13px;box-shadow:0 2px 6px rgba(0,0,0,.3)">${icon}</div>`,
    28
  );
}

function signalMarker(sig) {
  const col = SIG_COLOR[sig.statut] || '#C62828';
  return divIcon(
    `<div style="width:26px;height:26px;border-radius:4px;background:${col};border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-size:12px;box-shadow:0 2px 6px rgba(0,0,0,.3)">🚨</div>`,
    26
  );
}

function MapController({ center, zoom, bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length >= 2) {
      try {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
        return;
      } catch {}
    }
    map.setView(center, zoom);
  }, [center, zoom, bounds, map]);
  return null;
}

function extractLngLat(geom) {
  if (!geom) return null;
  if (typeof geom === 'object' && geom.coordinates) return geom.coordinates;
  return null;
}

function pointFromLatLng(item) {
  if (item.geom?.coordinates) return item.geom.coordinates;
  if (item.longitude != null && item.latitude != null) return [item.longitude, item.latitude];
  return null;
}

export default function Carte() {
  const { country, countryCode } = useCountry();
  const { profile } = useAuth();
  const { scopeCommuneId } = useTenantScope();
  const isSuper = profile?.role === 'superadmin';
  const [searchParams, setSearchParams] = useSearchParams();
  const focus = searchParams.get('focus');
  const [infras, setInfras] = useState([]);
  const [signaux, setSignaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [center, setCenter] = useState(country.map.defaultCenter);
  const [zoom, setZoom] = useState(country.map.defaultZoom);
  const [autoFit, setAutoFit] = useState(true);
  const [showInfra, setShowInfra] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [showClean, setShowClean] = useState(false);
  const [cleanOpts, setCleanOpts] = useState({ signalements: 'resolved', infrastructures: false, scopeCountry: true });
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    setCenter(country.map.defaultCenter);
    setZoom(country.map.defaultZoom);
  }, [country.code]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [infraRes, sigRes] = await Promise.all([
          refApi.infrastructures(countryCode, scopeCommuneId || undefined),
          signalApi.list({ country_mode: countryCode, commune_id: scopeCommuneId || undefined, limit: 500 }),
        ]);
        if (!mounted) return;
        setInfras(infraRes.data || []);
        setSignaux(sigRes.data || []);
      } catch (e) {
        if (mounted) setError(formatApiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [countryCode, refresh, scopeCommuneId]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const filteredSignaux = useMemo(() => {
    // Résolu / Rejeté / À valider = non affichés sur la carte
    const open = signaux.filter((s) => !['resolu', 'rejete', 'a_valider'].includes(s.statut));
    if (focus === 'today') return open.filter((s) => (s.created_at || '').startsWith(todayStr));
    return open;
  }, [signaux, focus, todayStr]);

  const bounds = useMemo(() => {
    if (!autoFit) return null;
    const pts = [];
    if (focus !== 'today' && showInfra) {
      infras.forEach((i) => {
        const c = pointFromLatLng(i);
        if (c) pts.push([c[1], c[0]]);
      });
    }
    filteredSignaux.forEach((s) => {
      const c = pointFromLatLng(s);
      if (c) pts.push([c[1], c[0]]);
    });
    return pts.length >= 2 ? pts : pts.length === 1 ? [pts[0], pts[0]] : null;
  }, [infras, filteredSignaux, autoFit, focus, showInfra]);

  const counts = useMemo(() => {
    let infraOnMap = 0;
    let sigOnMap = 0;
    infras.forEach((i) => { if (pointFromLatLng(i)) infraOnMap += 1; });
    filteredSignaux.forEach((s) => { if (pointFromLatLng(s)) sigOnMap += 1; });
    return { infraOnMap, sigOnMap, infraTotal: infras.length, sigTotal: filteredSignaux.length };
  }, [infras, filteredSignaux]);

  const runClean = async () => {
    setCleaning(true);
    setError('');
    try {
      const { data } = await adminApi.clearMapData({
        signalements: cleanOpts.signalements,
        infrastructures: cleanOpts.infrastructures,
        country_mode: cleanOpts.scopeCountry ? countryCode : null,
      });
      setShowClean(false);
      setRefresh((x) => x + 1);
      const d = data?.deleted || {};
      window.alert(`Nettoyage effectué : ${d.signalements || 0} signalement(s) et ${d.infrastructures || 0} infrastructure(s) supprimé(s).`);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="h-screen flex flex-col" data-testid="carte-page">
      <Topbar liveBadge={false} />

      <div className="flex items-center gap-2 px-4 py-2 bg-white border-b border-gray-200 text-xs">
        <span className="font-medium text-gray-700">🗺️ Carte —</span>
        {focus === 'today' && (
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-semibold text-white"
            style={{ background: country.palette.primary }}
            data-testid="map-filter-today"
          >
            📅 Aujourd'hui uniquement
            <button onClick={() => setSearchParams({})} className="ml-1 opacity-80 hover:opacity-100" data-testid="map-filter-clear">✕</button>
          </span>
        )}
        <button
          className="px-3 py-1 rounded-md hover:bg-gray-100"
          onClick={() => { setAutoFit(false); setCenter(country.map.defaultCenter); setZoom(country.map.defaultZoom); }}
          data-testid="map-btn-country"
        >
          {country.name} complet
        </button>
        <button
          className="px-3 py-1 rounded-md hover:bg-gray-100"
          onClick={() => { setAutoFit(false); setCenter(country.map.cityCenter); setZoom(country.map.cityZoom); }}
          data-testid="map-btn-city"
        >
          {country.map.cityName}
        </button>
        <button
          className={`px-3 py-1 rounded-md hover:bg-gray-100 ${autoFit ? 'bg-blue-50 text-blue-900 font-semibold' : ''}`}
          onClick={() => setAutoFit(true)}
          data-testid="map-btn-fit"
        >
          🎯 Ajuster aux données
        </button>
        <button
          className={`px-3 py-1 rounded-md transition ${showInfra ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-blue-600 text-white font-semibold'}`}
          onClick={() => setShowInfra((v) => !v)}
          data-testid="map-btn-toggle-infra"
          title="Afficher ou masquer les infrastructures pour libérer la carte"
        >
          {showInfra ? '🏗️ Masquer infrastructures' : '🏗️ Afficher infrastructures'}
        </button>
        {isSuper && (
          <button
            className="px-3 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-semibold"
            onClick={() => setShowClean(true)}
            data-testid="map-btn-clean"
            title="Supprimer définitivement les données d'exemple"
          >
            🧹 Nettoyer
          </button>
        )}
        <span className="ml-3 text-gray-500" data-testid="map-counts">
          {showInfra ? `${counts.infraOnMap}/${counts.infraTotal} infrastructures` : 'infrastructures masquées'} · {counts.sigOnMap}/{counts.sigTotal} signalements
        </span>
        {loading && <span className="ml-auto text-gray-500">Chargement…</span>}
        {error && <span className="ml-auto text-red-600">⚠️ {error}</span>}
      </div>

      <div className="flex-1 relative">
        <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom data-testid="leaflet-map">
          <MapController center={center} zoom={zoom} bounds={bounds} />
          <TileLayer
            attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {focus !== 'today' && showInfra && infras.map((inf) => {
            const c = pointFromLatLng(inf);
            if (!c) return null;
            return (
              <Marker key={`i-${inf.id}`} position={[c[1], c[0]]} icon={infraMarker(inf, country)}>
                <Popup>
                  <strong>{inf.nom}</strong>
                  <br />
                  Type : {inf.infrastructure_type?.nom || inf.type_code || '—'}
                  <br />
                  Statut : <span style={{ color: STATUS_COLOR[inf.statut], fontWeight: 600 }}>{inf.statut}</span>
                </Popup>
              </Marker>
            );
          })}
          {filteredSignaux.map((sig) => {
            const c = pointFromLatLng(sig);
            if (!c) return null;
            return (
              <Marker key={`s-${sig.id}`} position={[c[1], c[0]]} icon={signalMarker(sig)}>
                <Popup>
                  <strong>🚨 {sig.titre}</strong>
                  <br />
                  Type : {sig.type || '—'}
                  <br />
                  Statut : <span style={{ color: SIG_COLOR[sig.statut], fontWeight: 600 }}>{sig.statut}</span>
                  <br />
                  Priorité : <strong>{sig.priorite}</strong>
                  <br />
                  {sig.commune_nom || ''}
                  <br />
                  {new Date(sig.created_at).toLocaleString(country.labels.locale)}
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
        <div className="absolute bottom-6 right-4 bg-white/95 rounded-xl shadow-lg px-4 py-3 text-xs z-[1000]" data-testid="map-legend">
          <div className="font-semibold mb-2">Statut infrastructure</div>
          {INFRA_STATUSES.slice(0, 4).map((s) => (
            <div key={s.value} className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
              {s.label}
            </div>
          ))}
          <div className="font-semibold mt-3 mb-2">Signalement</div>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5" style={{ background: '#C62828' }} />
            Nouveau
          </div>
        </div>
      </div>

      {showClean && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[2000] p-4" data-testid="map-clean-modal">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">🧹 Nettoyer la carte</h2>
              <button onClick={() => setShowClean(false)} className="text-gray-500 hover:text-gray-900" data-testid="map-clean-close">✕</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-xs text-gray-500">Suppression définitive des données pour libérer la carte avant vos tests. Cette action est irréversible.</p>
              <div>
                <div className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-2">Signalements</div>
                <div className="space-y-1.5">
                  {[{ v: 'resolved', l: 'Supprimer les signalements résolus' }, { v: 'all', l: 'Supprimer TOUS les signalements' }, { v: 'none', l: 'Ne pas toucher aux signalements' }].map((o) => (
                    <label key={o.v} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input type="radio" name="clean-sig" checked={cleanOpts.signalements === o.v} onChange={() => setCleanOpts({ ...cleanOpts, signalements: o.v })} data-testid={`map-clean-sig-${o.v}`} />
                      {o.l}
                    </label>
                  ))}
                </div>
              </div>
              <div className="border-t border-gray-100 pt-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={cleanOpts.infrastructures} onChange={(e) => setCleanOpts({ ...cleanOpts, infrastructures: e.target.checked })} data-testid="map-clean-infra" />
                  Supprimer aussi les infrastructures d'exemple
                </label>
              </div>
              <div className="border-t border-gray-100 pt-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={cleanOpts.scopeCountry} onChange={(e) => setCleanOpts({ ...cleanOpts, scopeCountry: e.target.checked })} data-testid="map-clean-scope" />
                  Limiter au pays courant ({country.name})
                </label>
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">⚠️ {error}</div>}
              <div className="flex gap-2 pt-1">
                <button onClick={() => setShowClean(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50" data-testid="map-clean-cancel">Annuler</button>
                <button onClick={runClean} disabled={cleaning} className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-50 hover:bg-red-700" data-testid="map-clean-confirm">
                  {cleaning ? 'Suppression…' : 'Supprimer définitivement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
